import { useState } from 'react'
import { router } from '@inertiajs/react'
import { AlertTriangle, Cpu, Save, Settings, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import { CatalogStatusBar, type CatalogStatus } from '@/frontend/components/Litellm/CatalogStatusBar'
import { ModelChipList } from '@/frontend/components/Litellm/ModelChipList'
import { ModelPickerPopover } from '@/frontend/components/Litellm/ModelPickerPopover'
import type { LitellmModel } from '@/frontend/components/Litellm/types'
import type { FleetMachineSummary } from '@/frontend/types/fleetMachine'
import { FLEET_MACHINE_ENVIRONMENT_LABELS } from '@/frontend/types/fleetMachine'

type WhatsAppUnderlyingProvider = 'salvy' | 'twilio'
type LlmProvider = 'bedrock' | 'litellm'
type FallbackTarget = 'openai' | 'bedrock' | 'anthropic' | 'litellm'

interface AppSettings {
  default_whatsapp_underlying_provider: WhatsAppUnderlyingProvider
  default_llm_provider: LlmProvider
  default_fallback_target: FallbackTarget
  litellm_default_models: string[]
  default_fleet_machine_id: string | null
  maintenance_mode: boolean
  maintenance_message: string | null
}

interface ProviderOption<T extends string> {
  value: T
  label: string
  description: string
}

interface Props {
  settings: AppSettings
  litellm_catalog: LitellmModel[]
  litellm_catalog_status: CatalogStatus
  fleet_machine_options: FleetMachineSummary[]
  whatsapp_provider_options: ProviderOption<WhatsAppUnderlyingProvider>[]
  llm_provider_options: ProviderOption<LlmProvider>[]
  fallback_target_options: ProviderOption<FallbackTarget>[]
}

function OptionButton<T extends string>({
  option,
  active,
  onClick,
}: {
  option: ProviderOption<T>
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-4 text-left transition-colors ${
        active
          ? 'border-foreground bg-accent text-accent-foreground'
          : 'border-border bg-background hover:bg-accent/60'
      }`}
    >
      <span className="block text-sm font-semibold">{option.label}</span>
      <span className="mt-1 block text-xs text-muted-foreground">{option.description}</span>
    </button>
  )
}

function Show({
  settings,
  litellm_catalog,
  litellm_catalog_status,
  fleet_machine_options,
  whatsapp_provider_options,
  llm_provider_options,
  fallback_target_options,
}: Props) {
  const [provider, setProvider] = useState<WhatsAppUnderlyingProvider>(settings.default_whatsapp_underlying_provider)
  const [llmProvider, setLlmProvider] = useState<LlmProvider>(settings.default_llm_provider)
  const [fallbackTarget, setFallbackTarget] = useState<FallbackTarget>(settings.default_fallback_target)
  const [defaultModels, setDefaultModels] = useState<string[]>(settings.litellm_default_models)
  const [defaultFleetMachineId, setDefaultFleetMachineId] = useState(settings.default_fleet_machine_id ?? '')
  const [maintenanceMode, setMaintenanceMode] = useState(settings.maintenance_mode)
  const [maintenanceMessage, setMaintenanceMessage] = useState(settings.maintenance_message ?? '')
  const [saving, setSaving] = useState(false)

  const submit = () => {
    setSaving(true)
    router.patch('/cc/settings', {
      default_whatsapp_underlying_provider: provider,
      default_llm_provider: llmProvider,
      default_fallback_target: fallbackTarget,
      litellm_default_models: defaultModels,
      default_fleet_machine_id: defaultFleetMachineId,
      maintenance_mode: maintenanceMode,
      maintenance_message: maintenanceMessage,
    }, {
      preserveScroll: true,
      onFinish: () => setSaving(false),
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Settings className="h-6 w-6 text-foreground" />
          <div>
            <h2 className="text-2xl font-bold text-foreground">Configurações</h2>
            <p className="text-sm text-muted-foreground">Configurações operacionais globais do Workstation.</p>
          </div>
        </div>
        <Button type="button" size="sm" onClick={submit} disabled={saving}>
          <Save className="h-4 w-4" />
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>

      <section className="rounded-lg border bg-card p-4">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-foreground">Provedor padrão do WhatsApp</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Usado quando um cliente conecta WhatsApp no colaborador. O cliente não escolhe provider no dashboard.
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {whatsapp_provider_options.map((option) => (
            <OptionButton
              key={option.value}
              option={option}
              active={provider === option.value}
              onClick={() => setProvider(option.value)}
            />
          ))}
        </div>
      </section>

      <section className="rounded-lg border bg-card p-5">
        <div className="mb-4 flex items-start gap-3">
          <div className="rounded-md border border-border bg-muted/50 p-2">
            <Cpu className="h-5 w-5 text-foreground" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Provider LLM padrão</h3>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Define qual provider novas instâncias <code className="font-mono text-xs">fleet_api</code> herdam.
              Bedrock e LiteLLM são caminhos independentes — cada agente pode trocar depois na aba LLM.
            </p>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {llm_provider_options.map((option) => (
            <OptionButton
              key={option.value}
              option={option}
              active={llmProvider === option.value}
              onClick={() => setLlmProvider(option.value)}
            />
          ))}
        </div>
      </section>

      <section className="rounded-lg border bg-card p-5">
        <div className="mb-4 flex items-start gap-3">
          <div className="rounded-md border border-border bg-muted/50 p-2">
            <Cpu className="h-5 w-5 text-foreground" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Fallback de modelo padrão</h3>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Define o alvo de fallback que novos colaboradores herdam para a chain de modelos do agente.
              Cada colaborador pode sobrescrever depois na aba LLM.
            </p>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          {fallback_target_options.map((option) => (
            <OptionButton
              key={option.value}
              option={option}
              active={fallbackTarget === option.value}
              onClick={() => setFallbackTarget(option.value)}
            />
          ))}
        </div>
      </section>

      {llmProvider === 'litellm' && (
        <section className="rounded-lg border bg-card p-5">
          <div className="mb-4 flex items-start gap-3">
            <div className="rounded-md border border-border bg-muted/50 p-2">
              <Sparkles className="h-5 w-5 text-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-foreground">Catálogo LiteLLM Gateway</h3>
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                Modelos default herdados por novas instâncias <code className="font-mono text-xs">fleet_api</code>.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <CatalogStatusBar catalog={litellm_catalog} status={litellm_catalog_status} refreshUrl="/cc/settings/refresh_litellm_catalog" />

            <div>
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <Label className="text-sm font-medium text-foreground">Modelos default das novas instâncias</Label>
                  <p className="mt-1 max-w-xl text-xs text-muted-foreground">
                    Ordem importa: primeiro vira primary, demais entram na fallback chain. Cada agente pode reordenar.
                  </p>
                </div>
                <ModelPickerPopover
                  catalog={litellm_catalog}
                  selectedNames={defaultModels}
                  onPick={(name) => setDefaultModels([...defaultModels, name])}
                  label="Adicionar modelo"
                />
              </div>

              <ModelChipList
                selected={defaultModels}
                catalog={litellm_catalog}
                onChange={setDefaultModels}
                showPrimary
                catalogAvailable={litellm_catalog.length > 0}
                emptyHint="Adicione ao menos um modelo. Sem default, novas instâncias caem para a constante hardcoded."
              />
            </div>
          </div>
        </section>
      )}

      <section className="rounded-lg border bg-card p-4">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-foreground">Máquina padrão da frota</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Usada por novas organizações e por colaboradores criados em organizações sem máquina própria.
          </p>
        </div>
        <div className="max-w-xl">
          <Label htmlFor="default-fleet-machine">Máquina</Label>
          <select
            id="default-fleet-machine"
            value={defaultFleetMachineId}
            onChange={(event) => setDefaultFleetMachineId(event.target.value)}
            className="mt-2 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">Sem máquina padrão</option>
            {fleet_machine_options.map((machine) => (
              <option key={machine.id} value={machine.id}>
                {machine.name} - {machine.slug} - {FLEET_MACHINE_ENVIRONMENT_LABELS[machine.environment]}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${maintenanceMode ? 'text-red-500' : 'text-muted-foreground'}`} />
              <h3 className="text-base font-semibold text-foreground">Modo administrativo</h3>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Quando ativo, usuários não admins são bloqueados e webhooks de WhatsApp são aceitos com 200 sem encaminhar para as instâncias.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="maintenance-mode" className="text-sm">Ativo</Label>
            <Switch id="maintenance-mode" checked={maintenanceMode} onCheckedChange={setMaintenanceMode} />
          </div>
        </div>

        <div className="mt-4">
          <Label htmlFor="maintenance-message">Mensagem pública</Label>
          <textarea
            id="maintenance-message"
            value={maintenanceMessage}
            onChange={(event) => setMaintenanceMessage(event.target.value)}
            className="mt-2 min-h-24 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
            placeholder="Estamos em manutenção. Tente novamente em alguns minutos."
          />
        </div>
      </section>
    </div>
  )
}

Show.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default Show
